import { Drawer, Image } from "antd";
import { Fragment } from "react";
import { Sparkles, X, Calendar, ExternalLink, Megaphone, Zap, Bug, Gift } from "lucide-react";

interface ChangelogDrawerProps {
    visible?: boolean;
    onClose?: () => void;
}

type ChangelogType = "feature" | "improvement" | "fix" | "announcement";

interface ChangelogEntry {
    title: string;
    date: string;
    description: React.ReactNode;
    imageUrl?: string;
    link: string;
    type?: ChangelogType;
}

const ChangelogDrawer = ({
    visible,
    onClose
}: ChangelogDrawerProps) => {
    const changelog: ChangelogEntry[] = [
        {
            title: "Email Alerts for Build Minutes and Deployments",
            date: "July 10, 2023",
            type: "feature",
            description: <>
                We're thrilled to introduce a powerful enhancement to our platform that ensures your workflows run smoothly without any hiccups! Now, you'll receive timely email alerts when <b>you reach 70% of your build minutes and/or deployment limit</b>, as well as if you exceed them.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/w8qZQgABnrBwjKhKTkcfe_image.png",
            link: "https://docs.microtica.com/changelog#9HxyL"
        },
        {
            title: "Maximize Your Cloud Savings with Powerful Cost Optimization Tools",
            date: "June 15, 2023",
            type: "feature",
            description: <>
                We're thrilled to unveil our latest update: Microtica's Cost Optimization feature. As a cloud delivery platform on AWS, we understand the importance of efficient cost management. With this new feature, we're putting the power in your hands to optimize your cloud spending.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/kLDNrfd48ta4olJKjWKsK_image.png",
            link: "https://docs.microtica.com/changelog#ky0v_"
        },
        {
            title: "Pipeline Logging: Introducing Pipeline Steps and Step Execution Outputs",
            date: "May 18, 2023",
            type: "feature",
            description: <>
                This month we've worked on some exciting updates to our product's logging capabilities that will improve the way you monitor and track your workflows. Our pipeline logs now provide comprehensive details about pipeline steps and their corresponding execution outputs.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/9EEi2RAx_NyIRGME5c9ET_image.png",
            link: "https://docs.microtica.com/changelog#ocZf9"
        },
        {
            title: "Improvements and fixes",
            date: "May 2023",
            type: "fix",
            description: <ul>
                <li>Improvements in the Add custom domain flow.</li>
                <li>Optimized data refresh on pods (containers) in App Overview.</li>
            </ul>,
            link: "https://docs.microtica.com/changelog#JpNP5"
        },
        {
            title: "The Medusa template now includes Backend + Admin",
            date: "Apr 28, 2023",
            type: "announcement",
            description: <>
                We are delighted to announce that our existing Medusa Server (backend) template now includes the Admin dashboard, as supported by Medusa. With this new addition, our users can now take advantage of both the Medusa Backend and Medusa Admin, which together provide a complete e-commerce solution.
            </>,
            link: "https://docs.microtica.com/changelog#KL39_"
        },
        {
            title: "Announcing the improved pipeline logs",
            date: "Apr 19, 2023",
            type: "improvement",
            description: <>
                We are excited to announce a significant improvement to our logs feature at Microtica! Our enhanced logs feature now provides a clear distinction between successful and error logs, making it easier for you to quickly identify and troubleshoot issues.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/OLAlxGj9WxDHH0XbiF-G9_image.png",
            link: "https://docs.microtica.com/changelog#ntgAU"
        },
        {
            title: "n8n template with a default MySQL database",
            date: "Apr 19, 2023",
            type: "feature",
            description: <>
                We are excited to announce that Microtica now automatically includes MySQL as the default database for our n8n template! With this new addition, our users can now benefit from MySQL's excellent performance, reliability, and scalability.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/qYp1pCbG08OscYS31FbcK_pasted-graphic-3.png",
            link: "https://docs.microtica.com/changelog#SpVBo"
        },
        {
            title: "NextJS Template upgrade to v13",
            date: "Apr 7, 2023",
            type: "improvement",
            description: <>
                We are pleased to announce that Microtica has upgraded our Next.js template from version 12 to version 13, which is a major release! With this upgrade, our users can take advantage of the latest features and improvements offered by Next.js.
            </>,
            link: "https://docs.microtica.com/changelog#0CuxX"
        },
        {
            title: "Assign a custom domain for API-based components",
            date: "Mar 20, 2023",
            type: "feature",
            description: <>
                We've extended our custom domain feature to API-based components and resources in the platform. This specifically affects the Lambda (Golang) template.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/-YHp_t2EPzeMH5clUs_G7_image.png",
            link: "https://docs.microtica.com/changelog#5OeRm"
        },
        {
            title: "Improvements and fixes",
            date: "Mar 13, 2023",
            type: "fix",
            description: <ul>
                <li>You're now able to get the logs for a specific container inside a Kubernetes pod</li>
                <li>Add property MIC_CONTAINER_PORT in the Environment Variables section.</li>
                <li>Fixed a bug where the schema defaults were not pre-set when the resource was added to an environment.</li>
            </ul>,
            link: "https://docs.microtica.com/changelog#_Fq5Y"
        },
        {
            title: "Introducing Appwrite production-ready template",
            date: "Feb 27, 2023",
            type: "announcement",
            description: <>
                You can now deploy a production-ready Appwrite backend server on your AWS account following 3 simple steps. The template includes VPC, EKS Cluster, and the Appwrite application.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/bPzJuuIicv5kkHbtPtVkg_image.png",
            link: "https://docs.microtica.com/changelog#GV-vp"
        },
        {
            title: "Google Signup",
            date: "Feb 23, 2023",
            type: "feature",
            description: <>
                Microtica now supports easy signup and sign-in with Google. This update was inspired by our growing user base that uses a Google-based private or company account.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/4F-zjIVMcHaRzbboB_Mb2_image.png",
            link: "https://docs.microtica.com/changelog#DtMzz"
        },
        {
            title: "Optimized deployment pipelines",
            date: "Feb 15, 2023",
            type: "improvement",
            description: <>
                We've been working on optimizing the integrated CI/CD pipelines, and are proud to share that our newest release offers <b>faster builds and deployments by 60%</b>. You can deliver product versions faster than ever!
            </>,
            link: "https://docs.microtica.com/changelog#vFlam"
        },
        {
            title: "Assign a custom domain on Applications and Resources",
            date: "Jan 13, 2023",
            type: "feature",
            description: <>
                With this update we are dramatically simplifying the custom domain setup for your applications and cloud resources. To assign a custom domain just go to your application or resource details and click on Assign domain button.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/Ug59-RXL_mlI2PwwnMFkx_image.png?auto=format&ixlib=react-9.1.1&w=1153&h=517&dpr=1&q=75",
            link: "https://docs.microtica.com/changelog#Q2F6C"
        },
        {
            title: "Pipeline builds for your applications",
            date: "Dec 2, 2022",
            type: "feature",
            description: <>
                We are integrating application pipeline build and deployment executions in the Application Pipeline section. Now you will be able to navigate through pipeline execution history.
            </>,
            imageUrl: "https://archbee.imgix.net/pJMDLZwhjs4SKnSajyB-i/929d4SKzVCLMiUJJHQnlc_image.png?auto=format&ixlib=react-9.1.1&w=2292&h=1406&dpr=2&q=50",
            link: "https://docs.microtica.com/changelog#VdJS9"
        },
    ];

    const getTypeIcon = (type?: ChangelogType) => {
        switch (type) {
            case "feature":
                return <Gift size={12} />;
            case "improvement":
                return <Zap size={12} />;
            case "fix":
                return <Bug size={12} />;
            case "announcement":
                return <Megaphone size={12} />;
            default:
                return <Sparkles size={12} />;
        }
    };

    const getTypeLabel = (type?: ChangelogType) => {
        switch (type) {
            case "feature":
                return "New Feature";
            case "improvement":
                return "Improvement";
            case "fix":
                return "Bug Fix";
            case "announcement":
                return "Announcement";
            default:
                return "Update";
        }
    };

    return (
        <Drawer
            placement="right"
            open={visible}
            destroyOnClose={true}
            closable={false}
            onClose={onClose}
            width={440}
            className="changelog-drawer"
            styles={{
                header: { display: 'none' },
                body: { padding: 0 }
            }}
        >
            {/* Custom Header */}
            <div className="changelog-drawer-header">
                <div className="changelog-drawer-header-content">
                    <div className="changelog-drawer-title-row">
                        <div className="changelog-drawer-icon">
                            <Sparkles size={20} />
                        </div>
                        <div className="changelog-drawer-title-content">
                            <h2 className="changelog-drawer-title">What's New</h2>
                            <span className="changelog-drawer-subtitle">Latest updates & features</span>
                        </div>
                    </div>
                    <button 
                        className="changelog-drawer-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            {/* Changelog List */}
            <div className="changelog-drawer-list">
                {changelog.map((log, index) => (
                    <div className="changelog-entry" key={index}>
                        <div className="changelog-entry-header">
                            <span className={`changelog-entry-type changelog-entry-type-${log.type || 'update'}`}>
                                {getTypeIcon(log.type)}
                                {getTypeLabel(log.type)}
                            </span>
                            <span className="changelog-entry-date">
                                <Calendar size={12} />
                                {log.date}
                            </span>
                        </div>
                        <h3 className="changelog-entry-title">{log.title}</h3>
                        <div className="changelog-entry-description">
                            {log.description}
                        </div>
                        {log.imageUrl && (
                            <div className="changelog-entry-image">
                                <Image 
                                    width="100%" 
                                    src={log.imageUrl} 
                                    style={{ borderRadius: 8 }}
                                    preview={{ mask: 'Click to preview' }}
                                />
                            </div>
                        )}
                        <a 
                            href={log.link} 
                            target="_blank" 
                            rel="noreferrer"
                            className="changelog-entry-link"
                        >
                            <span>Read more in the Changelog</span>
                            <ExternalLink size={14} />
                        </a>
                    </div>
                ))}
            </div>
        </Drawer>
    )
}

export default ChangelogDrawer;
